import { useCallback, useEffect, useState } from 'react';
import { leadgenApi, type LeadGenLandingPage } from '@/api/leadgen.api';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { showApiError } from '@/utils/apiError';
import { notify } from '@/core/toast/notify';
import { usePermissions } from '@/hooks/usePermissions';

export function LeadGenLandingPagesPage() {
  const { hasAny } = usePermissions();
  const canManage = hasAny('LEAD_GEN_MANAGE');
  const [pages, setPages] = useState<LeadGenLandingPage[]>([]);
  const [title, setTitle] = useState('');
  const [slug, setSlug] = useState('');
  const [templateId, setTemplateId] = useState('demo-template');
  const [customDomain, setCustomDomain] = useState('');
  const [abTestVariant, setAbTestVariant] = useState('A');

  const load = useCallback(async () => {
    try {
      setPages(await leadgenApi.listLandingPages());
    } catch (err) {
      showApiError(err, 'Failed to load landing pages');
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!title.trim() || !slug.trim()) return;
    try {
      await leadgenApi.createLandingPage({
        title,
        slug,
        templateId,
        customDomain,
        abTestVariant,
        published: true,
        version: 1,
        viewsCount: 0,
        conversionsCount: 0
      });
      notify.success('Landing page created & published');
      setTitle('');
      setSlug('');
      setCustomDomain('');
      load();
    } catch (err) {
      showApiError(err, 'Failed to create landing page');
    }
  };

  return (
    <div className="page-content">
      <PageHeader
        title="Landing Page Builder"
        description="Launch high-converting landing pages with native A/B testing and custom domain support."
        breadcrumbs={[
          { label: 'Org', to: '/org' },
          { label: 'Lead Gen', to: '/lead-gen' },
          { label: 'Landing Pages' },
        ]}
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {canManage && (
        <div className="lg:col-span-2 card p-5 space-y-4">
          <h3 className="section-title">Create Landing Page</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input label="Page Title" placeholder="e.g. Free Enterprise Audit Trial" value={title} onChange={(e) => setTitle(e.target.value)} />
            <Input label="URL Slug" placeholder="e.g. enterprise-audit" value={slug} onChange={(e) => setSlug(e.target.value)} />
            <Input label="Custom Domain" placeholder="e.g. promo.mycompany.com" value={customDomain} onChange={(e) => setCustomDomain(e.target.value)} />
            <Select
              label="A/B Variant"
              value={abTestVariant}
              onChange={(v) => setAbTestVariant(v)}
              options={[
                { value: 'A', label: 'Variant A (Default Layout)' },
                { value: 'B', label: 'Variant B (Interactive Form Side)' },
                { value: 'C', label: 'Variant C (Minimalistic Text Only)' },
              ]}
            />
          </div>
          <Select
            label="Landing Page Template"
            value={templateId}
            onChange={(v) => setTemplateId(v)}
            options={[
              { value: 'demo-template', label: 'Interactive Product Demo Signup' },
              { value: 'newsletter-template', label: 'Newsletter Subscription Panel' },
              { value: 'quote-template', label: 'Finance & Quotation Estimator' },
            ]}
          />
          <Button className="w-full" onClick={create}>Publish Landing Page</Button>
        </div>
        )}

        <div className={`card p-5 space-y-4 ${canManage ? '' : 'lg:col-span-3'}`}>
          <h3 className="section-title">Active Landing Pages</h3>
          <ul className="space-y-4">
            {pages.map(p => (
              <li key={p.id} className="border border-slate-100 rounded-lg p-4 bg-slate-50 space-y-2">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="font-bold text-slate-800">{p.title}</p>
                    <p className="text-xs text-slate-400">/{p.slug} · Version {p.version}</p>
                  </div>
                  <span className="text-xs px-2 py-0.5 rounded bg-blue-100 text-blue-800">Variant {p.abTestVariant}</span>
                </div>
                <div className="grid grid-cols-2 gap-2 text-center pt-2">
                  <div className="rounded border border-slate-200 bg-white p-2 dark:border-slate-700 dark:bg-slate-800">
                    <p className="text-xs text-slate-400">Views</p>
                    <p className="font-bold text-slate-800">{p.viewsCount}</p>
                  </div>
                  <div className="rounded border border-slate-200 bg-white p-2 dark:border-slate-700 dark:bg-slate-800">
                    <p className="text-xs text-slate-400">Conversions</p>
                    <p className="font-bold text-slate-800">{p.conversionsCount}</p>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}
